import { useTranslation } from "react-i18next";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AnalystExhibitAppearance, analystExhibitFrameClassName } from "./analystExhibitFrame";
import { formatAnalystValue } from "./formatAnalystValue";

import type { AnalystTableSpec } from "@digital-twins/shared-types";

interface AnalystTableProps {
  table: AnalystTableSpec;
  index: number;
  appearance?: AnalystExhibitAppearance;
}

function isValidTable(table: AnalystTableSpec): boolean {
  if (
    table.schemaVersion !== "analyst-table.v1" ||
    !Array.isArray(table.columns) ||
    table.columns.length === 0 ||
    table.columns.length > 50 ||
    !Array.isArray(table.rows) ||
    table.rows.length === 0 ||
    table.rows.length > 200
  ) {
    return false;
  }

  const columnIds = table.columns.map((column) => column.id);
  const rowIds = table.rows.map((row) => row.id);
  if (
    new Set(columnIds).size !== columnIds.length ||
    new Set(rowIds).size !== rowIds.length ||
    table.columns.some(
      (column) => !column.id || !column.label || !column.unit,
    )
  ) {
    return false;
  }

  const referencedFactIds = table.rows.flatMap((row) =>
    row.cells.map((cell) => cell.evidenceFactId),
  );
  const referencedFactIdSet = new Set(referencedFactIds);
  const evidenceFactIdSet = new Set(table.evidenceFactIds);
  if (
    evidenceFactIdSet.size !== table.evidenceFactIds.length ||
    referencedFactIdSet.size !== referencedFactIds.length ||
    referencedFactIds.length !== table.evidenceFactIds.length ||
    referencedFactIds.some((factId) => !evidenceFactIdSet.has(factId))
  ) {
    return false;
  }

  return table.rows.every((row) => {
    const cellsByColumn = new Map(
      row.cells.map((cell) => [cell.columnId, cell]),
    );
    return (
      row.id.length > 0 &&
      row.label.length > 0 &&
      row.cells.length === columnIds.length &&
      cellsByColumn.size === columnIds.length &&
      columnIds.every((columnId) => {
        const cell = cellsByColumn.get(columnId);
        return (
          cell !== undefined &&
          cell.evidenceFactId.length > 0 &&
          Number.isInteger(cell.baseN) &&
          cell.baseN >= 0 &&
          (cell.suppressionThreshold === undefined ||
            (Number.isInteger(cell.suppressionThreshold) &&
              cell.suppressionThreshold >= 0)) &&
          (cell.baseN >= (cell.suppressionThreshold ?? 8) ||
            cell.isSuppressed) &&
          (cell.isSuppressed
            ? cell.value === null
            : typeof cell.value === "number" && Number.isFinite(cell.value))
        );
      })
    );
  });
}

export function AnalystTable(props: AnalystTableProps) {
  const { table, index, appearance } = props;
  const { t } = useTranslation("app");

  if (!isValidTable(table)) {
    return (
      <div
        className="rounded-xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground"
        data-testid={`analyst-table-invalid-${index}`}
      >
        {t("studyReport.analyst.tableInvalid")}
      </div>
    );
  }

  return (
    <figure
      className={`min-w-0 ${analystExhibitFrameClassName(appearance)}`}
      data-testid={`analyst-table-${index}`}
    >
      <figcaption className="flex flex-col gap-1">
        <span className="text-base font-semibold text-foreground">{table.title}</span>
        {table.subtitle && (
          <span className="text-sm text-muted-foreground">{table.subtitle}</span>
        )}
      </figcaption>
      <div className="mt-4 max-h-96 min-w-0 overflow-auto rounded-lg border border-border/70">
        <Table className="min-w-max" data-testid={`analyst-table-grid-${index}`}>
          <TableHeader className="sticky top-0 z-10 bg-card">
            <TableRow>
              <TableHead className="min-w-40 bg-card">
                {t("studyReport.analyst.tableGroup")}
              </TableHead>
              {table.columns.map((column) => (
                <TableHead
                  key={column.id}
                  className="min-w-32 bg-card text-right"
                >
                  <span className="flex flex-col gap-0.5">
                    <span className="block max-w-48 whitespace-normal text-left">{column.label}</span>
                    <span className="text-[11px] font-normal">
                      {column.unit}
                    </span>
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.rows.map((row) => {
              const cellsByColumn = new Map(
                row.cells.map((cell) => [cell.columnId, cell]),
              );
              return (
                <TableRow key={row.id}>
                  <TableCell className="max-w-48 whitespace-normal font-medium text-foreground">
                    {row.label}
                  </TableCell>
                  {table.columns.map((column) => {
                    const cell = cellsByColumn.get(column.id)!;
                    return (
                      <TableCell
                        key={column.id}
                        className="text-right tabular-nums"
                      >
                        <span className="flex flex-col gap-0.5">
                          <span>
                            {cell.isSuppressed || cell.value === null
                              ? t("studyReport.analyst.suppressed")
                              : formatAnalystValue(cell.value, column.valueFormat)}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {cell.isSuppressed
                              ? t("studyReport.analyst.tableSuppressedN")
                              : t("studyReport.analyst.tableBaseN", {
                                  count: cell.baseN,
                                })}
                          </span>
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </figure>
  );
}
